import { useState, useEffect, useMemo, useCallback } from "react"
import { db } from "lib/firebase"
import { doc, onSnapshot } from "firebase/firestore"
import { FAMILY_MEMBERS } from "utils/userMapping"

/**
 * Aggregation hook that listens to all family members' travel data
 * and computes union sets for the Family tab.
 */
export const useFamilyTravelData = (active) => {
  const [memberData, setMemberData] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!active) return

    const unsubscribes = []
    let loadedCount = 0

    FAMILY_MEMBERS.forEach((name) => {
      const docId = name.toLowerCase()
      const unsub = onSnapshot(
        doc(db, "travel", docId),
        (snapshot) => {
          const data = snapshot.exists() ? snapshot.data() : {}
          setMemberData((prev) => ({
            ...prev,
            [name]: {
              usa: data.usa || [],
              world: data.world || [],
            },
          }))
          loadedCount++
          if (loadedCount >= FAMILY_MEMBERS.length) {
            setLoading(false)
          }
        },
        (error) => {
          console.error(`Error fetching travel data for ${name}:`, error)
          loadedCount++
          if (loadedCount >= FAMILY_MEMBERS.length) {
            setLoading(false)
          }
        },
      )
      unsubscribes.push(unsub)
    })

    return () => unsubscribes.forEach((unsub) => unsub())
  }, [active])

  const familyUsa = useMemo(() => {
    const allStates = new Set()
    Object.values(memberData).forEach(({ usa }) => {
      usa.forEach((s) => allStates.add(s))
    })
    return [...allStates]
  }, [memberData])

  const familyWorld = useMemo(() => {
    const allCountries = new Set()
    Object.values(memberData).forEach(({ world }) => {
      world.forEach((c) => allCountries.add(c))
    })
    return [...allCountries]
  }, [memberData])

  const getVisitorsForPlace = useCallback(
    (scope, placeId) => {
      const field = scope === "usa" ? "usa" : "world"
      return Object.entries(memberData)
        .filter(([, data]) => data[field].includes(placeId))
        .map(([name]) => name)
    },
    [memberData],
  )

  return { familyUsa, familyWorld, getVisitorsForPlace, loading }
}
